<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Vehículos</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative"
        >
          <div class="w-full w-md-auto md-flex-1">
            <div
              class="d-flex justify-content-between align-items-center justify-content-lg-start"
            >
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section
            class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0"
          >
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por número de placa, marca o modelo"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />

              <div class="text-right mt-2 mt-md-0">
                <b-button
                  :to="{ name: 'config-vehicles-create' }"
                  variant="none"
                  class="button button-primary ml-md-2"
                  >Agregar</b-button
                >
              </div>
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div
          class="w-100 d-flex flex-wrap align-items-center mt-2"
          v-if="showFiltersList"
        >
          <span
            v-for="(val, name) in filterList"
            v-bind:key="name"
            class="filter-item"
          >
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Placa</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>
                Tipo de <br />
                vehículo
              </th>
              <th>Capacidad máxima</th>
              <th>Propiedad</th>
              <th>Pertenece a</th>
              <th>Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="text-center">{{ o.license_plate }}</td>
              <td class="text-center">{{ getBrand(o.brand).label }}</td>
              <td class="text-center">{{ o.model }}</td>
              <td class="text-center">{{ getVehicleType(o.vehicle_type_id) }}</td>
              <td class="text-center">
                <div v-if="getVehicleTypeObj(o.vehicle_type_id)">
                  <span class="mr-2">
                    <i class="ri-group-fill"></i>
                    {{ getVehicleTypeObj(o.vehicle_type_id).capacity }}
                  </span>
                  <span>
                    <i class="ri-briefcase-3-line"></i>
                    {{ getVehicleTypeObj(o.vehicle_type_id).max_baggage }}
                  </span>
                </div>
                <span v-else>-</span>
              </td>
              <td class="text-center">{{ getVehicleProperty(o.ownership_type).label }}</td>
              <!-- Dueño del vehículo (DEF-ADM-018): TravelCab o un proveedor. -->
              <td class="text-center">
                <span v-if="o.provider_name" class="badge badge-green">{{ o.provider_name }}</span>
                <span v-else class="badge badge-blue">TravelCab</span>
              </td>
              <td class="text-center">
                <span :class="`badge ${getStatus(o.status).color}`">{{
                  getStatus(o.status).label
                }}</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem
                      @click="
                        $router.push({
                          name: 'config-vehicles-edit',
                          params: { id: o.id },
                        })
                      "
                      text="Editar"
                    />
                    <DropdownItem
                      @click="
                        $router.push({
                          name: 'config-vehicle-shifts',
                          params: { id: o.id },
                        })
                      "
                      text="Turnos"
                    />
                    <DropdownItem
                      :text="getStatus(o.status).action"
                      @click="openChangeStatus(o)"
                    />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages" :changePage="changePage"
          :changePageSize="changePageSize" v-if="list.length" />

        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import { VehicleService, VehicleTypeService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

import {
  Alert,
  Constants,
  Util,
  PaginationMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  FilterMenu,
  FilterMixin,
  ConfirmationModal,
  ConfirmationModalMixin
} from "wize-admin";

export default {
  components: {
    SearchBar,
    FilterMenu,
    DropdownTable,
    DropdownItem,
    ConfirmationModal
  },
  mixins: [
    PaginationMixin,
    FilterMixin,
    ConfirmationModalMixin
  ],
  data() {
    return {
      webUrl: process.env.VUE_APP_WEB_URL,
      list: [],
      tooltipType: null,
      vehicleTypes: [],
      filterOptions: [
        {
          label: "Tipo de vehículo",
          key: "type_id",
          type: Constants.TypeFilters.SINGLE,
          options: [],
        },
        {
          label: "Propiedad",
          key: "ownership_type",
          type: Constants.TypeFilters.SINGLE,
          options: [
            { key: "1", label: "TRAVELCAB" },
            { key: "2", label: "EXTERNO"   },
          ],
        },
        {
          label: "Estado",
          key: "status",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.VehicleStatus.ACTIVE,
            LocalConstants.VehicleStatus.INACTIVE,
          ],
        }
      ],
    };
  },
  methods: {
    openChangeStatus(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title: "Cambiar estado",
        description: "¿Está seguro que desea cambiar el estado?",
        acceptText: "Aceptar",
        action: "update",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "update") this.changeStatus();
    },
    async changeStatus() {
      try {
        await this.$store.dispatch("app/loading", true);

        let status = this.selectedOption.status;
        if (status == LocalConstants.VehicleStatus.ACTIVE.key) {
          status = LocalConstants.VehicleStatus.INACTIVE.key;
        } else if (status == LocalConstants.VehicleStatus.INACTIVE.key) {
          status = LocalConstants.VehicleStatus.ACTIVE.key;
        }

        await VehicleService.changeStatus(this.selectedOption.id, status);
        await this.loadData();
        Alert.success("Estado actualizado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors.message);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);

        const params = {page: this.page - 1., size: this.pageSize, sort: 'id'};
        const data = {search: this.search, ...this.filters};

        const response = await VehicleService.list(data, params);
        this.list = JSON.parse(JSON.stringify(response.payload?.items));
        this.total = response.payload?.metadata.totalElements;
        this.pages = response.payload?.metadata.totalPages;

      } catch (error) {
        console.error(error);
        Alert.error(error);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    getBrand(key) {
      return LocalConstants.VehicleBrands.find(item => item.key == key) || { label: key || '-' };
    },
    getStatus(key) {
      let keyInt = parseInt(key);
      return Util.searchValue(keyInt, LocalConstants.VehicleStatus);
    },
    getVehicleType(key) {
      let vehicleType = this.vehicleTypes.find((vt) => vt.id == key);
      return vehicleType ? vehicleType.name : '-';
    },
    getVehicleTypeObj(key) {
      return this.vehicleTypes.find((vt) => vt.id == key) || null;
    },
    getVehicleProperty(key) {
      return Util.searchValue(key, LocalConstants.VehicleProperty) || { label: "—" };
    }
  },
  async mounted() {
    await this.loadData();

    this.vehicleTypes = (await VehicleTypeService.getAll()).payload;
    const vehicleTypeOption = this.filterOptions.find((o) => o.key === "type_id");
    this.vehicleTypes.forEach((b) => vehicleTypeOption.options.push({ key: b.id, label: b.name }));
  },
};
</script>

<style lang="stylus" scoped></style>
